// Shared helpers for compliance briefs — used by both the live DiscoveryView
// and the read-only SavedRunView so the two stay in lockstep (DRY).

/** Strip a leading "<uuid>_" prefix that ingestion prepends to filenames. */
export const stripUuid = (name) => (name || "").replace(/^[0-9a-f-]{36}_/i, "");

/** Trigger a client-side file download for generated text content. */
export function download(filename, text, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Render a discovered/compliance brief as a Markdown document. */
export function briefToMarkdown(brief) {
  const lines = [
    `# Compliance Brief — ${stripUuid(brief.document_name)}`,
    `**Typ:** ${brief.document_type || "—"}`,
    "",
  ];
  for (const s of brief.sections || []) {
    const title = s.title || s.label || s.key;
    if ((s.kind || "tabular") === "prose") {
      lines.push(`## ${title}`, "");
      if (s.description) lines.push(`_${s.description}_`, "");
      (s.requirements || []).forEach((r, i) => {
        lines.push(`${r.order || i + 1}. ${(r.text || "").replace(/\n/g, " ")} _(${r.source?.location || ""})_`);
      });
      lines.push("");
      continue;
    }
    const cols = [...(s.columns || []), "Quelle"];
    lines.push(`## ${title}`, "");
    if (s.description) lines.push(`_${s.description}_`, "");
    if (s.missing > 0) lines.push(`> ⚠ extracted ${s.extracted_cells}/${s.source_cells} — ${s.missing} missing`, "");
    lines.push("| " + cols.join(" | ") + " |");
    lines.push("| " + cols.map(() => "---").join(" | ") + " |");
    for (const r of s.rows || []) {
      const cells = (s.columns || []).map((c) => (r.cells?.[c] ?? "").toString().replace(/\n/g, " "));
      cells.push(r.source?.location || "");
      lines.push("| " + cells.join(" | ") + " |");
    }
    lines.push("");
  }
  if (brief.gaps?.length) lines.push("## Lücken / Gaps", ...brief.gaps.map((g) => `- ${g}`));
  return lines.join("\n");
}

function csvEscape(v) {
  const s = (v ?? "").toString();
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Render a discovered/compliance brief as CSV (one block per section). */
export function briefToCsv(brief) {
  const blocks = [];
  for (const s of brief.sections || []) {
    const title = s.title || s.label || s.key;
    if ((s.kind || "tabular") === "prose") {
      const rows = [`# ${title}`, ["Nr.", "Anforderung", "Quelle"].map(csvEscape).join(",")];
      (s.requirements || []).forEach((r, i) => {
        rows.push([csvEscape(r.order || i + 1), csvEscape(r.text), csvEscape(r.source?.location)].join(","));
      });
      blocks.push(rows.join("\n"));
      continue;
    }
    const cols = [...(s.columns || []), "Quelle"];
    const rows = [`# ${title}`, cols.map(csvEscape).join(",")];
    for (const r of s.rows || []) {
      const cells = (s.columns || []).map((c) => csvEscape(r.cells?.[c]));
      cells.push(csvEscape(r.source?.location));
      rows.push(cells.join(","));
    }
    blocks.push(rows.join("\n"));
  }
  return blocks.join("\n\n");
}

/** Fetch the server-built .xlsx for a document and trigger a download. */
export async function downloadExcel(apiBase, documentId, database, filename) {
  const res = await fetch(`${apiBase}/api/v1/approval/discover/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ document_id: documentId, database }),
  });
  if (!res.ok) throw new Error(`Excel export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "compliance_brief.xlsx";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
